import { groupBy, sumBy } from "lodash";
import moment from "moment";
import { all, put, select, takeLatest, call } from "redux-saga/effects";
import { enums, format, http } from "../../common/lib";
import config from "../../config";
import { logToSentry } from "../../config/Sentry";
import actions from "../actions";
import { actionCreators, actionTypes } from "./actions";
import {
    getAuditsToCreate,
    getAuditLocationsToCreate,
    getAuditAssetsToCreate,
    getAuditAssignmentsToCreate
} from "./sagaUtils";
// pre-assemble all table data available
//
// Wrapped in try/catch like save() and get() below. Every saga runs under one
// `all([...])` in root-saga.js, so an uncaught throw here (a failed
// location-asset-count POST, a dropped connection) did not just fail this
// step: it stopped the root saga and every saga in the app with it, and step 1
// of the wizard sat on its spinner until the page was reloaded.
function* init() {
    try {
        const { branches, locations, branchLocations, userLocations } =
            yield select((state) => state);
        const { id } = yield select((state) => state.userOrganisations.item);
        const uniqueBranches = [];
        const branchLocation = [];

        branchLocations.data.map((item, index) => {
            if (!uniqueBranches.includes(item.branch)) {
                uniqueBranches.push(item.branch);
                branchLocation.push({ branch: item.branch });
            }
        });
        const url = `${config.api.paths.locationAssetCount}?organisation=${id}`;
        const branchData = yield call(http.authorized.post, url, branchLocation);

        const data = branchLocations.data.map((branch, index) => {
            const branchName = branches.data
                .filter((el) => el.id === branch.branch)
                .map((i) => i.name);

            const branchRef = branches.data
                .filter((el) => el.id === branch.branch)
                .map((i) => (i.branchRef.length > 0 ? i.branchRef[0].name : ""));

            const users = userLocations.data.filter(
                (item) =>
                    branch.location === item.location &&
                    item.group === enums.auth.group.auditor
            );

            let locationName = locations.data.filter(
                (item) => item.id === branch.location
            );
            locationName = locationName.length > 0 ? locationName[0].name : "";

            const date = format.dateTimeNew(moment().toISOString());

            // A branch missing from the count response has no countable assets;
            // reading .count off undefined here used to throw out of the saga.
            const assetsCount =
                branchData?.data?.find((item) => item.branchId === branch.branch)
                    ?.count ?? 0;

            return {
                id: index,
                auto: true,
                branch: branch.branch,
                branchName: branchName,
                branchRef: branchRef,
                expected: 0,
                live: 0,
                location: branch.location,
                locationName,
                mandatory: 0,
                assetsCount,
                selected: false,
                name: `${branchName}`,
                // name: `${branchName}/${date}`,
                userLocations: users
            };
        });

        yield put(actionCreators.update({ data, initError: false }));
    } catch (error) {
        logToSentry("On audit create init:", { error });
        yield put(actionCreators.update({ initError: true }));
    }
}

function* save(action) {
    try {
        const { auditGroups, auditsCreate, auth, userOrganisations } =
            yield select((state) => state);

        const createdBy = auth.account.id;
        let res;

        /** 1.  On auditsTo Create */
        const auditsToCreate = getAuditsToCreate(
            auditsCreate.data,
            auditsCreate,
            createdBy,
            auditGroups.item.id,
            userOrganisations.item.id
        );

        /** Post audits api to create  audits */
        res = yield call(
            http.authorized.post,
            config.api.paths.audits,
            auditsToCreate
        );
        const audits = res.data;

        /** 2.  Creates location for all the audits
         * param 1 auditsToCreate and param 2 result of post
         */
        const auditLocationsToCreate = getAuditLocationsToCreate(
            auditsToCreate,
            audits
        );
        /** Post all locations to create   */
        res = yield call(
            http.authorized.post,
            config.api.paths.auditLocations,
            auditLocationsToCreate
        );
        const auditLocations = res.data;

        /** 3.  Creates assignments for all the audits
         * param 1 auditsToCreate and param 2 result of post auditLocation
         * param 3 auditLocationResult from post and  param 4 createdBy user
         */
        const auditAssignmentsToCreate = getAuditAssignmentsToCreate(
            auditsToCreate,
            audits,
            auditLocations,
            createdBy
        );
        /** Post assignments To create */
        yield call(
            http.authorized.post,
            config.api.paths.auditAssignments,
            auditAssignmentsToCreate
        );

        yield all([
            put(actions.audits.create.postSuccess(audits)),
            put(actions.auditsCreate.create.saveSuccess(audits))
        ]);
    } catch (error) {
        console.error(error);
        logToSentry("On audits create save:", { error });
        yield put(actionCreators.saveFailure(error));
    }
}

function* summary(action) {
    const { auditAssets, auditsCreate } = yield select((state) => state);
    const byBranch = groupBy(
        auditAssets.data.filter((item) => item.isRelated),
        (item) => item.branch
    );
    const data = auditsCreate.data.map((item) => {
        const forBranchId = byBranch[item.branch] || [];
        return {
            ...item,
            // Additional logic required due to lodash bug that returns boolean value when the sum equals 1
            expected:
                sumBy(forBranchId, (i) => i.isExpected === true) === true
                    ? 1
                    : sumBy(forBranchId, (i) => i.isExpected === true),
            live:
                sumBy(forBranchId, (i) => i.isLive === true) === true
                    ? 1
                    : sumBy(forBranchId, (i) => i.isLive === true),
            mandatory:
                sumBy(forBranchId, (i) => i.isMandatory === true) === true
                    ? 1
                    : sumBy(forBranchId, (i) => i.isMandatory === true)
        };
    });
    yield put(actionCreators.update({ data }));
}

function* get(action) {
    const { id } = yield select((state) => state.userOrganisations.item);
    try {
        const { branchLocation } = action.payload;

        const url = `${config.api.paths.locationAssetCount}?organisation=${id}`;
        const { data } = yield call(http.authorized.post, url, branchLocation);

        yield put(actionCreators.getCountSuccess(data));
    } catch (error) {
        logToSentry("On audit locations get count:", { error });
        yield put(actionCreators.getCountFailure(error));
    }
}

export default function* () {
    yield all([
        takeLatest(actionTypes.AUDITS_CREATE_INIT, init),
        takeLatest(actionTypes.AUDITS_CREATE_SAVE_REQUEST, save),
        takeLatest(actionTypes.AUDITS_CREATE_SUMMARY, summary),
        takeLatest(actionTypes.AUDIT_LOCATIONS_GET_COUNT_REQUEST, get)
    ]);
}
